import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { SocialConnectPurpose, SocialProvider } from '@nucleus-commerce/api-client/admin';

import { openAuthorization } from '../api/open-authorization';
import { socialApi } from '../api/social-api';

import { socialKeys } from './social-keys';

/**
 * Connect, reconnect and the ads consent all start the same way: the backend answers the
 * network's authorization URL and the browser leaves for it. The callback lands back on the
 * Channels page with `?connected=` or `?error=`, so nothing here touches the cache.
 */
export function useConnectSocial(storeId: string, provider: SocialProvider) {
  return useMutation({
    mutationFn: (purpose: SocialConnectPurpose) => socialApi.connect(storeId, provider, purpose),
    onSuccess: ({ url }) => openAuthorization(url),
  });
}

/** Revokes on the network, drops the tokens, switches catalogue sync and ads off; posts stay. */
export function useDisconnectSocial(storeId: string, provider: SocialProvider) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => socialApi.disconnect(storeId, provider),
    meta: { successMessage: 'social:toasts.disconnected', silentError: true },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: socialKeys.catalog(storeId, provider) });
      queryClient.removeQueries({ queryKey: socialKeys.adAccounts(storeId, provider) });
      return queryClient.invalidateQueries({ queryKey: socialKeys.connections(storeId) });
    },
  });
}
